import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const realtimeAnalysisArticle: BlogArticle = {
  key: BlogArticleKey.REALTIME_ANALYSIS,
  slug: BlogSlugs.REALTIME_ANALYSIS,
  categoryId: "01",
  date: "2026-09-02",
  readTime: 7,
  locales: {
    fr: {
      title: "Analyser ses appels en temps réel ou après coup ?",
      excerpt:
        "Le temps réel souffle pendant l'appel, l'analyse après coup fait ressortir ce qui revient. Les deux ne résolvent pas le même problème, et un seul se cumule.",
      description:
        "Le temps réel souffle pendant l'appel, l'analyse après coup montre ce qui revient. Ce que chacun résout, et lequel te sert vraiment.",
      ctaTitle: "Vois ce qui revient dans tes appels",
      ctaText:
        "On lit tes appels analysés et on en sort les objections, les questions et les formulations qui reviennent, consolidées sur l'ensemble. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La question revient dès qu'on compare des outils d'analyse d'appels : est-ce qu'il faut quelque chose qui parle pendant l'appel, ou quelque chose qui travaille une fois qu'il est fini ?",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les deux existent, les deux fonctionnent, et ils ne résolvent pas le même problème. Choisir sur la fiche technique mène presque toujours au mauvais outil.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que le temps réel résout vraiment",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un outil en temps réel écoute pendant que tu parles et affiche des éléments à l'écran : une réponse à une objection qui vient d'être formulée, un rappel de tarif, une question à poser ensuite.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Sa valeur réelle est une valeur de formation. Il sert à quelqu'un qui ne connaît pas encore le produit, l'argumentaire ou les objections du marché : un commercial arrivé il y a trois semaines, un fondateur qui vend une offre nouvelle. Il compense un manque de réflexes au moment exact où ce manque coûte cher.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Le temps réel ne rend pas meilleur, il évite de se tromper pendant qu'on apprend.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Et ce qu'il coûte",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Il partage ton attention.** Lire une suggestion pendant qu'une personne parle, c'est cesser de l'écouter. Le prospect le sent, et c'est exactement ce qu'un bon appel ne doit pas produire.",
            "**Il suppose une intégration vivante.** Il faut que l'outil soit dans la visio, avec les autorisations qui vont avec, et que tout le monde l'accepte.",
            "**Il ne garde rien de cumulé.** Il t'aide sur cet appel-là. Le suivant repart de zéro, et rien ne dit ce qui revient d'un appel à l'autre.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que l'analyse après coup résout",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une analyse post-appel ne t'aide pas sur l'appel qui vient de finir. Elle t'aide sur les vingt suivants, parce qu'elle est la seule à pouvoir comparer.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une objection entendue une fois est une anecdote. La même objection sur douze appels est un défaut : de ton offre, de ton discours, ou de la cible que tu appelles. Ce passage de l'anecdote au motif ne se fait pas pendant l'appel, par construction, puisqu'il faut plusieurs appels pour l'observer.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ce que le temps réel ne peut pas faire",
            items: [
              "Dire ce qui revient sur trois mois",
              "Nourrir ton ICP avec les mots des clients",
              "Repérer l'objection que tu contournes à chaque fois",
              "Servir à écrire tes messages de prospection",
            ],
          },
          good: {
            title: "Ce que l'après-coup ne peut pas faire",
            items: [
              "T'aider au moment où tu bloques",
              "Rattraper un appel déjà mal parti",
              "Former quelqu'un dès sa première semaine",
              "Remplacer la préparation d'un appel",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "Comment choisir, concrètement",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Tu intègres des commerciaux régulièrement.** Le temps réel a du sens : il raccourcit le temps où une nouvelle recrue coûte des affaires.",
            "**Tu es seul ou vous êtes deux ou trois.** Tu connais ton produit mieux que n'importe quelle suggestion. Ce qui te manque n'est pas un souffleur, c'est de la mémoire.",
            "**Tu cherches à corriger ton offre ou ton ciblage.** Seule l'analyse cumulée répond, parce que la réponse est dans la répétition, pas dans un appel.",
            "**Tu veux réutiliser ce qui se dit en appel dans tes messages.** Là encore, il faut la matière consolidée : les mots exacts, les objections, les déclencheurs.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les deux approches ne s'excluent d'ailleurs pas. Mais si tu n'en prends qu'une, prends celle qui se cumule : le temps réel s'évapore avec l'appel, l'analyse consolidée vaut plus chaque mois.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Meidly analyse après l'appel, pas pendant",
          text: "C'est un choix, pas une limite technique : ce qui sert à corriger une offre, un ciblage ou un message se lit sur des dizaines d'appels, jamais sur un seul. Meidly lit tes enregistrements et consolide ce qui revient.",
          linkLabel: "Découvrir l'analyse d'appels",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
      ],
      faq: [
        {
          question: "Le temps réel est-il plus performant que l'analyse après coup ?",
          answer:
            "Ni l'un ni l'autre, ils répondent à deux questions différentes. Le temps réel aide une personne qui manque encore de réflexes, sur l'appel en cours. L'analyse après coup fait apparaître ce qui se répète d'un appel à l'autre, ce qu'aucun outil ne peut voir pendant un appel isolé.",
        },
        {
          question: "Meidly propose-t-il de l'analyse en temps réel ?",
          answer:
            "Non. Meidly analyse des appels enregistrés puis consolide les objections, les questions et les formulations sur l'ensemble de tes appels. C'est cette vue cumulée qui alimente ensuite l'avatar client, les critères de ciblage et les messages.",
        },
        {
          question: "Peut-on utiliser les deux ?",
          answer:
            "Oui, ils ne se gênent pas : l'un accompagne pendant l'appel, l'autre travaille après. Si le budget n'en permet qu'un, la question à se poser est de savoir si ton problème est de bien mener un appel ou de comprendre ce qui revient dans tous.",
        },
        {
          question: "Un souffleur en direct se voit-il côté prospect ?",
          answer:
            "Les suggestions ne s'affichent que de ton côté, mais la coupure d'attention, elle, se perçoit. Lire pendant que quelqu'un parle s'entend dans les silences et les relances à côté de la plaque.",
        },
      ],
    },
    en: {
      title: "Real-time or post-call analysis: which do you need?",
      excerpt:
        "Real-time prompts you during the call, post-call analysis surfaces what keeps coming back. They solve different problems, and only one compounds.",
      description:
        "Real-time prompts you during the call, post-call analysis shows what keeps coming back. What each one solves, and which you need.",
      ctaTitle: "See what keeps coming back in your calls",
      ctaText:
        "We read your analysed calls and pull out the objections, questions and wordings that recur, consolidated across the set. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The question comes up the moment you start comparing call analysis tools: do you want something that talks during the call, or something that works once it is over?",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Both exist, both work, and they do not solve the same problem. Choosing on the feature list almost always leads to the wrong tool.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What real-time actually solves",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A real-time tool listens while you speak and puts things on screen: an answer to the objection just raised, a pricing reminder, the question to ask next.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Its real value is a training value. It serves someone who does not yet know the product, the pitch or the market's objections: a rep who started three weeks ago, a founder selling a new offer. It compensates for missing reflexes at the exact moment that gap is expensive.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Real-time does not make you better, it keeps you from getting it wrong while you learn.",
        },
        {
          type: ArticleBlockType.H2,
          text: "And what it costs",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**It splits your attention.** Reading a suggestion while someone is talking means you stopped listening. The prospect feels it, and that is exactly what a good call must not produce.",
            "**It assumes a live integration.** The tool has to sit inside the meeting, with the permissions that go with it, and everyone has to accept it.",
            "**It keeps nothing cumulative.** It helps you on that call. The next one starts from zero, and nothing tells you what recurs from one call to another.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "What post-call analysis solves",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Post-call analysis does not help with the call that just ended. It helps with the next twenty, because it is the only one that can compare.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An objection heard once is an anecdote. The same objection across twelve calls is a defect: in your offer, in your pitch, or in who you are calling. Moving from anecdote to pattern cannot happen during a call, by construction, since it takes several calls to observe.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "What real-time cannot do",
            items: [
              "Tell you what recurs across three months",
              "Feed your ICP with your clients' own words",
              "Spot the objection you talk around every time",
              "Give you the material to write your outreach",
            ],
          },
          good: {
            title: "What post-call cannot do",
            items: [
              "Help you at the moment you are stuck",
              "Rescue a call that already went wrong",
              "Train someone in their first week",
              "Replace preparing for a call",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "How to choose, concretely",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**You onboard reps regularly.** Real-time makes sense: it shortens the window where a new hire costs you deals.",
            "**You are alone, or two or three.** You know your product better than any suggestion. What you lack is not a prompter, it is memory.",
            "**You want to fix your offer or your targeting.** Only cumulative analysis answers that, because the answer is in the repetition, not in one call.",
            "**You want to reuse what is said on calls in your outreach.** Again, you need the consolidated material: the exact words, the objections, the triggers.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The two approaches do not exclude each other. But if you only take one, take the one that compounds: real-time evaporates with the call, consolidated analysis is worth more every month.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Meidly analyses after the call, not during",
          text: "That is a choice, not a technical limit: what lets you fix an offer, a target or a message is read across dozens of calls, never a single one. Meidly reads your recordings and consolidates what recurs.",
          linkLabel: "Discover call analysis",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
      ],
      faq: [
        {
          question: "Is real-time better than post-call analysis?",
          answer:
            "Neither, they answer two different questions. Real-time helps someone who still lacks reflexes, on the call in progress. Post-call analysis surfaces what repeats from one call to the next, which no tool can see inside a single call.",
        },
        {
          question: "Does Meidly offer real-time analysis?",
          answer:
            "No. Meidly analyses recorded calls, then consolidates the objections, questions and wordings across the whole set. That cumulative view is what then feeds the client avatar, the targeting criteria and the messages.",
        },
        {
          question: "Can you use both?",
          answer:
            "Yes, they do not get in each other's way: one assists during the call, the other works afterwards. If the budget only allows one, the question is whether your problem is running a call well or understanding what recurs across all of them.",
        },
        {
          question: "Can the prospect tell a live prompter is running?",
          answer:
            "The suggestions only show on your side, but the break in attention does come across. Reading while someone speaks is audible in the pauses and in follow-up questions that land beside the point.",
        },
      ],
    },
  },
};
